// ═══════════════════════════════════════════════════════════════════════════
// Adapter: backend connector-registry manifests → legacy marketplace shape.
//
// The ConnectorsPage UI (and anything else that depended on
// MARKETPLACE_CONNECTORS / MARKETPLACE_CATEGORIES) was originally fed by a
// hand-maintained frontend file. With M1 Unification the backend registry is
// the single source of truth — this adapter produces the exact shape the UI
// expects, derived from `/api/v1/connector-registry/manifests` and
// `/api/v1/connector-registry/categories`.
//
// Returns:
//   {
//     connectors: [...],   // MARKETPLACE_CONNECTORS-shaped
//     categories: [...],   // MARKETPLACE_CATEGORIES-shaped (id, name, icon, color, description)
//   }
// ═══════════════════════════════════════════════════════════════════════════

import { getCategoryStyle } from './marketplaceCategoryStyle';

function prettyCategoryName(rawId) {
  if (!rawId) return 'Uncategorized';
  return rawId
    .split('-')
    .map((p) => (p.length <= 3 ? p.toUpperCase() : p.charAt(0).toUpperCase() + p.slice(1)))
    .join(' ');
}

// ── Shape adapters ──────────────────────────────────────────────────────────
// The legacy ConnectorsPage (and anything consuming MARKETPLACE_CONNECTORS)
// expects a specific skill/guardrail/eval shape. Backend manifests use a
// slightly different vocabulary (displayName/description/appliesTo/metric).
// These helpers translate one to the other so the UI tabs render properly and
// downstream consumers (Agents "attach skill" flow, Canvas node palette) keep
// working without each call-site having to know the backend shape.

// backend severity (block/warn/info/allow) → UI severity (critical/high/medium/low)
function mapSeverity(raw) {
  switch ((raw || '').toLowerCase()) {
    case 'critical':
    case 'block':      return 'critical';
    case 'high':
    case 'warn':       return 'high';
    case 'medium':
    case 'info':       return 'medium';
    case 'low':
    case 'allow':      return 'low';
    default:           return 'medium';
  }
}

// Derive a UI eval "type" (functional/negative/guardrail/cost/security) from
// the backend eval's metric + tags. Keeps the icon/colour mapping in sync with
// EVAL_TYPE_COLORS in ConnectorsPage.
function deriveEvalType(ev) {
  const metric = (ev?.metric || '').toLowerCase();
  const tags = Array.isArray(ev?.tags) ? ev.tags.map((t) => String(t).toLowerCase()) : [];
  if (tags.includes('security') || metric.includes('auth') || metric.includes('injection')) return 'security';
  if (tags.includes('cost') || metric.includes('cost') || metric.includes('token')) return 'cost';
  if (tags.includes('guardrail') || metric.includes('guardrail') || metric === 'block_rate') return 'guardrail';
  if (tags.includes('negative') || metric.includes('error') || metric === 'error_rate') return 'negative';
  return 'functional';
}

// Convert a backend operation's inputs map ({ to: "array (required)", ... })
// into the params array shape ({name, type, required, example}) the UI needs.
function inputsToParams(inputs) {
  if (!inputs || typeof inputs !== 'object') return [];
  return Object.entries(inputs).map(([name, spec]) => {
    const raw = String(spec || '');
    // spec looks like "array (required)" or "string" etc.
    const typeMatch = raw.match(/^([a-zA-Z_]+)/);
    const required = /required/i.test(raw);
    return {
      name,
      type: typeMatch ? typeMatch[1] : 'string',
      required,
      example: undefined,
    };
  });
}

// Map backend skill → UI skill. Skills are conceptual capabilities in the
// backend manifest; the method/endpoint/params come from the matching
// operation. Matching priority: (1) explicit operationId field, (2) same id,
// (3) shared canonicalNodeType, (4) first operation. Prefer (1)/(2) so that
// connectors with many skills sharing one canonicalNodeType (e.g. 12 Slack
// skills all under MESSAGE_SEND) each resolve to their own op.
function mapSkill(s, operations) {
  if (!s) return null;
  const ops = Array.isArray(operations) ? operations : [];
  const nodeTypes = Array.isArray(s.canonicalNodeTypes) ? s.canonicalNodeTypes : [];

  // 1) explicit link via skill.operationId
  let op = s.operationId ? ops.find((o) => o?.id === s.operationId) : null;
  // 2) same id (common when skills are 1:1 with ops)
  if (!op) op = ops.find((o) => o?.id === s.id);
  // 3) shared canonicalNodeType
  if (!op) {
    op = ops.find((o) =>
      Array.isArray(o?.canonicalNodeTypes) &&
      o.canonicalNodeTypes.some((n) => nodeTypes.includes(n))
    );
  }
  // 4) fallback to first op
  if (!op) op = ops[0] || {};

  // If skill carries its own method/endpoint/params (rich-skill manifests that
  // embed operation details on the skill itself), prefer those over the op's.
  const method   = s.method   || op.method   || '';
  const endpoint = s.endpoint || s.path || op.path || op.endpoint || '';
  const params   = Array.isArray(s.params) && s.params.length > 0
    ? s.params
    : inputsToParams(op.inputs);

  return {
    id: s.id,
    name: s.displayName || s.name || s.id,
    description: s.description || '',
    method,
    endpoint,
    params,
    // Skill-level inline guardrail hints — either the rich-skill list of
    // strings from the enriched manifests, or empty.
    guardrails: Array.isArray(s.guardrails) ? s.guardrails : [],
    samplePayload: s.samplePayload || null,
    credentialType: s.credentialType || null,
    accessDescription: s.accessDescription || null,
    requiredScopes: s.requiredScopes || null,
    // Pass-through metadata useful to Agents attach-skill flow + Canvas palette.
    canonicalNodeTypes: nodeTypes,
    category: s.category || null,
    version: s.version || null,
    tags: Array.isArray(s.tags) ? s.tags : [],
  };
}

// Backend guardrail → UI guardrail ({id, severity, rule}).
function mapGuardrail(g) {
  if (!g) return null;
  return {
    id: g.id,
    severity: mapSeverity(g.severity),
    rule: g.description || g.displayName || g.id,
    // Keep originals for downstream callers that want the richer shape.
    displayName: g.displayName || g.id,
    appliesTo: g.appliesTo || null,
    tags: Array.isArray(g.tags) ? g.tags : [],
  };
}

// Generate a sensible configSchema from auth.requiredFields + configDefaults.
// Backend manifests don't carry a per-field config schema, so we synthesize
// one so the Config tab in ConnectorsPage renders real admin-setup inputs.
// Heuristics:
//   - fields with names containing password/secret/token/key/credential → secret input, encrypted
//   - known OAuth fields (client_id, client_secret, tenant_id) get friendly labels + helpText
//   - port fields get numeric defaults
//   - other known names get sensible placeholders
function prettyFieldLabel(name) {
  if (!name) return '';
  return name
    // split camelCase / PascalCase into words
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())
    // fix up common all-caps acronyms after title-casing
    .replace(/\bId\b/g, 'ID')
    .replace(/\bApi\b/g, 'API')
    .replace(/\bUrl\b/g, 'URL')
    .replace(/\bUri\b/g, 'URI')
    .replace(/\bSso\b/g, 'SSO')
    .replace(/\bMfa\b/g, 'MFA')
    .replace(/\bSaml\b/g, 'SAML')
    .replace(/\bJwt\b/g, 'JWT')
    .replace(/\bSmtp\b/g, 'SMTP')
    .replace(/\bOauth\b/gi, 'OAuth');
}

function isSecretField(name) {
  const n = (name || '').toLowerCase();
  // G164 — added serviceaccountjson + privatekey_json so the GCP-style
  // service-account JSON paste field gets treated as a secret (encrypted,
  // masked, sentinel re-render on edit).
  return /password|secret|token|apikey|api_key|credential|privatekey|private_key|serviceaccountjson|service_account_json|privatekey_json|private_key_json/.test(n);
}

/** G164 — Detect fields whose value is naturally multi-line (JSON blobs, PEM
 * keys, certificate bundles). Lets the UI render a textarea instead of an
 * <input type=password> so users can paste a full service-account JSON or
 * private key. */
function isMultilineField(name) {
  const n = (name || '').toLowerCase();
  return /json|pem|certificate|cert$|private_key|privatekey/.test(n);
}

function inferFieldType(name) {
  const n = (name || '').toLowerCase();
  if (isMultilineField(n)) return 'textarea';
  if (isSecretField(n)) return 'secret';
  if (/port$/.test(n) || n === 'timeout' || /count$/.test(n)) return 'number';
  if (/url$|endpoint$|host$/.test(n)) return 'text';
  return 'text';
}

function buildConfigSchema(authRequiredFields, configDefaults) {
  const fields = Array.isArray(authRequiredFields) ? authRequiredFields : [];
  const defaults = configDefaults && typeof configDefaults === 'object' ? configDefaults : {};
  if (fields.length === 0) return [];

  return fields.map((f) => {
    const type = inferFieldType(f);
    const isSecret = isSecretField(f);
    const defVal = defaults[f];
    return {
      field: f,
      label: prettyFieldLabel(f),
      type,
      required: true,
      encrypted: isSecret,
      placeholder: isSecret ? '••••••••••••••••' : (defVal != null ? String(defVal) : ''),
      defaultValue: defVal != null && !isSecret ? String(defVal) : '',
      helpText: isSecret
        ? 'Stored encrypted in the tenant vault. Never exposed to agent context.'
        : '',
    };
  });
}

// Backend eval → UI eval ({name, type, description}).
function mapEval(ev) {
  if (!ev) return null;
  return {
    id: ev.id,
    name: ev.displayName || ev.id || 'eval',
    type: deriveEvalType(ev),
    description: ev.description || '',
    metric: ev.metric || null,
    expectedPassRate: ev.expectedPassRate != null ? ev.expectedPassRate : null,
    tags: Array.isArray(ev.tags) ? ev.tags : [],
  };
}

/**
 * Transform a single backend ConnectorManifest into the legacy marketplace
 * shape. The UI expects fields like {name, marketplaceCategory, authType,
 * baseUrl, docs}; the backend manifest nests some of these under `metadata`
 * and `auth`, so we flatten on the way out.
 */
export function manifestToMarketplaceConnector(m) {
  if (!m) return null;
  // Registry includes non-connector meta files (e.g. _categories.json) with no
  // id/displayName — skip them so the UI doesn't render blank cards.
  if (!m.id && !m.displayName) return null;
  const meta = m.metadata || {};
  const auth = m.auth || {};

  // Prefer first legacyId (e.g. "openai-llm-v1") so older installed-connector
  // matching by id keeps working during the transition.
  const legacyId = Array.isArray(m.legacyIds) && m.legacyIds.length > 0
    ? m.legacyIds[0]
    : m.id;

  const category = m.category || 'uncategorized';

  const rawOperations = Array.isArray(m.operations) ? m.operations : [];
  const rawSkills     = Array.isArray(m.skills) ? m.skills : [];
  const rawGuardrails = Array.isArray(m.guardrails) ? m.guardrails : [];
  const rawEvals      = Array.isArray(m.evals) ? m.evals : [];

  // If the manifest has zero skills but real operations, surface each
  // operation as a synthetic skill so the UI doesn't render an empty list.
  // This is the common case for connectors whose only "capability" is the
  // REST operations themselves (e.g. IAM, ticketing).
  const skillSource = rawSkills.length > 0
    ? rawSkills.map((s) => mapSkill(s, rawOperations))
    : rawOperations.map((op) => mapSkill(
        {
          id: op.id,
          displayName: op.displayName || op.id,
          description: op.description || '',
          canonicalNodeTypes: op.canonicalNodeTypes || [],
        },
        [op]
      ));

  // ── Auth: compose a rich authConfig + choose the best configSchema source ──
  // The backend manifest now carries the full legacy auth fidelity on
  // `auth` (grantType, authUrl, tokenUrl, scopes, fields, requiredRoles,
  // requiredPlugins, providerPresets, configSchema).  Pass them through as a
  // nested `authConfig` object matching the legacy connectorMarketplace.js
  // shape so UI paths that expect "oauth2 password grant" (ServiceNow) vs
  // "oauth2 authorization_code / Connect My Account" (Google SSO) render
  // correctly.
  const authConfig = (auth && (auth.grantType || auth.tokenUrl || auth.authUrl
      || (Array.isArray(auth.fields) && auth.fields.length > 0)
      || (Array.isArray(auth.scopes) && auth.scopes.length > 0)
      || auth.providerPresets))
    ? {
        grantType: auth.grantType || null,
        authUrl: auth.authUrl || null,
        tokenUrl: auth.tokenUrl || null,
        scopes: Array.isArray(auth.scopes) ? auth.scopes : [],
        fields: Array.isArray(auth.fields) ? auth.fields : [],
        requiredRoles: Array.isArray(auth.requiredRoles) ? auth.requiredRoles : [],
        requiredPlugins: Array.isArray(auth.requiredPlugins) ? auth.requiredPlugins : [],
        providerPresets: auth.providerPresets || null,
      }
    : null;

  // configSchema resolution order:
  //   1) manifest.configSchema (if a future manifest sets this top-level)
  //   2) auth.configSchema (new — populated by our auth-fidelity fix)
  //   3) synthesized from auth.requiredFields
  let resolvedConfigSchema;
  if (Array.isArray(m.configSchema) && m.configSchema.length > 0) {
    resolvedConfigSchema = m.configSchema;
  } else if (Array.isArray(auth.configSchema) && auth.configSchema.length > 0) {
    resolvedConfigSchema = auth.configSchema;
  } else {
    resolvedConfigSchema = buildConfigSchema(auth.requiredFields, m.configDefaults);
  }

  return {
    // Identity
    id: legacyId,                     // frontend uses legacy id for UI state
    manifestId: m.id,                 // keep backend id too
    legacyIds: Array.isArray(m.legacyIds) ? m.legacyIds : [],
    name: m.displayName || m.id,
    vendor: m.vendor || '',
    version: m.version || '1.0.0',
    // Categorization
    category: prettyCategoryName(category),   // "LLM Provider" etc. (legacy display)
    marketplaceCategory: category,             // kebab-case id ("llm-provider")
    // Presentation
    tier: m.tier || 'free',
    status: meta.status || 'active',
    icon: getCategoryStyle(category).icon,    // emoji icon for the card
    description: m.description || '',
    // Auth (legacy-shape compatible + rich authConfig for OAuth/SMTP flows)
    authType: auth.type || 'none',
    authFields: auth.requiredFields || [],
    authConfig,                                // full legacy authConfig or null
    // Connection metadata
    baseUrl: meta.baseUrl || '',
    rateLimits: m.rateLimits || {},
    docs: meta.docsUrl || '',
    tags: m.tags || [],
    // Capability envelope
    canonicalNodeTypes: m.canonicalNodeTypes || [],
    platformBindings: m.platformBindings || {},
    operations: rawOperations,
    models: m.models || [],
    // Shape-adapted capability arrays — match the legacy UI contract.
    skills: skillSource.filter(Boolean),
    guardrails: rawGuardrails.map(mapGuardrail).filter(Boolean),
    evals: rawEvals.map(mapEval).filter(Boolean),
    // configSchema preferred from backend (auth.configSchema) → synthesized fallback.
    configSchema: resolvedConfigSchema,
    // Scope groups for OAuth connectors — pass through if backend provides,
    // otherwise derive from auth.scopes so the scope-picker UI has something.
    //
    // UI contract: { group: string, scopes: string (space-joined), required: boolean, note?: string }.
    // Backend may send {label, scopes:[array]} or the legacy shape {group, scopes:string}
    // — normalize both into the legacy shape so ConnectorsPage renders cleanly.
    scopeGroups: (() => {
      const toUiGroup = (g) => {
        if (!g || typeof g !== 'object') return null;
        const scopesField = g.scopes;
        const scopesStr = Array.isArray(scopesField) ? scopesField.join(' ') : String(scopesField || '');
        return {
          id: g.id || g.group || g.label || 'scope',
          group: g.group || g.label || g.id || 'Scopes',
          scopes: scopesStr,
          required: g.required !== false, // default true if omitted
          note: g.note || undefined,
        };
      };
      if (Array.isArray(m.scopeGroups) && m.scopeGroups.length > 0) {
        return m.scopeGroups.map(toUiGroup).filter(Boolean);
      }
      if (Array.isArray(auth.scopes) && auth.scopes.length > 0) {
        return [{
          id: 'required',
          group: 'Required scopes',
          scopes: auth.scopes.join(' '),
          required: true,
        }];
      }
      return [];
    })(),
    // LLM-provider hint the UI keys off of
    isLlmProvider: category === 'llm-provider',
    // Config defaults for install form pre-fill
    configDefaults: m.configDefaults || {},
    // Backward-compat alias — some UI still reads `icon` as a URL
    vendorIcon: meta.vendorIcon || null,
    // Priority / milestone metadata for filtering
    priority: meta.priority || null,
    milestone: meta.milestone || null,
  };
}

/**
 * Build the MARKETPLACE_CATEGORIES-shaped array from the backend /categories
 * response, enriched with frontend styling (icon + color).
 */
export function categoriesToMarketplaceCategories(categories) {
  if (!Array.isArray(categories)) return [];
  return categories.map((c) => {
    const style = getCategoryStyle(c.id);
    return {
      id: c.id,
      name: c.displayName || prettyCategoryName(c.id),
      icon: style.icon,
      color: style.color,
      description: c.description || '',
      count: c.count || 0,
    };
  });
}

/**
 * One-shot builder: feed in the two API responses, get the merged marketplace
 * shape back. Safe to call with empty arrays — returns {connectors: [],
 * categories: []}.
 */
// ────────────────────────────────────────────────────────────────────────────
// Shared helper: resolve an installed connector record back to its marketplace
// manifest entry. The installed record can be legacy (pre-M1) so it often has
// mismatched `name` and `type` vs the current manifest — e.g. installed
// "ServiceNow SecOps" vs manifest "ServiceNow Security Operations". This
// helper walks through increasingly-loose matching strategies so ConnectorsPage
// (installed modal) and AgentsPage (Import from Connector dialog) share one
// source of truth.
// ────────────────────────────────────────────────────────────────────────────
const _norm = (s) => String(s || '').trim().toLowerCase();

// Produce a set of lowercase alphanumeric tokens, excluding common stopwords
// that add no identity value ("the", "and", "of" plus the suffix noise we
// commonly see on installed connector display names).
const _tokens = (s) => {
  const STOP = new Set(['the', 'and', 'of', 'for', 'via', 'api', 'rest', 'by']);
  return String(s || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t && !STOP.has(t));
};

export function findMarketplaceForInstalled(marketplaceConnectors, installed) {
  if (!installed || !Array.isArray(marketplaceConnectors) || marketplaceConnectors.length === 0) {
    return null;
  }
  const { name, url, type, description } = installed;
  const nName = _norm(name);
  const nUrl  = _norm(url);
  const nType = _norm(type);
  const nDesc = _norm(description);

  // 1) Exact id match against manifestId / legacy id / legacyIds
  if (nType) {
    const byId = marketplaceConnectors.find((m) => {
      if (_norm(m.manifestId) === nType) return true;
      if (_norm(m.id) === nType) return true;
      if (Array.isArray(m.legacyIds) && m.legacyIds.some((lid) => _norm(lid) === nType)) return true;
      return false;
    });
    if (byId) return byId;
  }

  // 2) Exact name match (case-insensitive)
  if (nName) {
    const byName = marketplaceConnectors.find((m) => _norm(m.name) === nName);
    if (byName) return byName;
  }

  // 3) Description match (exact)
  if (nDesc) {
    const byDesc = marketplaceConnectors.find((m) => _norm(m.description) === nDesc);
    if (byDesc) return byDesc;
  }

  // 4) Base URL prefix match (skip empty baseUrl)
  if (nUrl) {
    const byUrl = marketplaceConnectors.find((m) => {
      const mbu = _norm(m.baseUrl);
      return mbu && mbu !== '' && nUrl.startsWith(mbu);
    });
    if (byUrl) return byUrl;
  }

  // 5) Type prefix against manifest id — "servicenow_secops" → "servicenow"
  if (nType) {
    const byPrefix = marketplaceConnectors.find((m) =>
      (_norm(m.manifestId) && nType.startsWith(_norm(m.manifestId)))
      || (_norm(m.id) && nType.startsWith(_norm(m.id)))
    );
    if (byPrefix) return byPrefix;
  }

  // 6) Token-intersection match — "ServiceNow SecOps" ↔ "ServiceNow Security Operations"
  //    A safe fuzzy rule: the installed name and manifest name must share at
  //    least one distinctive token (≥4 chars) that ALSO appears as a substring
  //    in the manifest's normalized id/manifestId/legacyIds. This is tight
  //    enough to avoid spurious matches while catching common rename drift.
  //
  //    Tiebreaker: prefer the candidate with the largest token intersection
  //    so "Gmail" doesn't accidentally match a "Gmail Calendar" sibling.
  if (nName) {
    const itoks = new Set(_tokens(name));
    if (itoks.size > 0) {
      const candidates = [];
      for (const m of marketplaceConnectors) {
        const mtoks = new Set(_tokens(m.name));
        if (mtoks.size === 0) continue;
        const shared = [...itoks].filter((t) => mtoks.has(t) && t.length >= 4);
        if (shared.length === 0) continue;
        /* G165 — Restrict idSurface to the PRIMARY ids only. Strategy 1
         * already handles legacyIds via exact match for rename tolerance.
         * Letting legacyIds participate here caused cross-vendor false
         * positives — e.g. "Google Workspace SSO" (installed) matched
         * "Google Cloud Secret Manager" because the GCP manifest carried
         * legacyIds: ["google-secret-manager-v1"] which contains the
         * substring 'google'. */
        const idSurface = `${_norm(m.id)}|${_norm(m.manifestId)}`;
        const inId = shared.some((t) => idSurface.includes(t));
        if (!inId) continue;
        candidates.push({ m, score: shared.length });
      }
      if (candidates.length > 0) {
        candidates.sort((a, b) => b.score - a.score);
        return candidates[0].m;
      }
    }
  }

  return null;
}

export function buildMarketplaceFromRegistry(manifests, categories) {
  const mappedConnectors = (Array.isArray(manifests) ? manifests : [])
    .map(manifestToMarketplaceConnector)
    .filter(Boolean);

  const mappedCategories = categoriesToMarketplaceCategories(categories);

  // DEF-UI-CATEGORY-LABEL-001 — a connector's category LABEL must come from the
  // registry displayName ("CASB / SASE"), not the id-prettifier, which mangles
  // multi-word acronyms ("casb-sase" → "Casb Sase"). categoriesToMarketplace-
  // Categories already resolved displayName; reuse it as the single source of
  // truth so the card, facet, and detail all read the registry label.
  const catLabelById = new Map(mappedCategories.map((c) => [c.id, c.name]));
  mappedConnectors.forEach((c) => {
    const label = catLabelById.get(c.marketplaceCategory);
    if (label) c.category = label;
  });

  // Fall back: if backend didn't return a categories array (older server),
  // derive the id list from the manifests themselves so filter pills still
  // render.
  let finalCategories = mappedCategories;
  if (finalCategories.length === 0 && mappedConnectors.length > 0) {
    const seen = new Map();
    mappedConnectors.forEach((c) => {
      const id = c.marketplaceCategory;
      if (!id) return;
      if (!seen.has(id)) {
        const style = getCategoryStyle(id);
        seen.set(id, {
          id,
          name: prettyCategoryName(id),
          icon: style.icon,
          color: style.color,
          description: '',
          count: 0,
        });
      }
      const entry = seen.get(id);
      entry.count += 1;
    });
    finalCategories = Array.from(seen.values());
  }

  return { connectors: mappedConnectors, categories: finalCategories };
}
